import { composeRefHandlers, isDefined } from "@utils/general";
import { capitalize } from "@utils/string";
import React from "react";
import { withTranslation } from "react-i18next";

import { IconSize } from "../../../enums";
import { KeyName } from "../../../keyName";
import DateType, { DateFormat, getUtcDayjs } from "../../../types/Date";
import { IInputProps } from "../input";
import { DatePickerClean, IPickerProps } from "./DatePicker";
import { ICustomReferenceElement } from "./DatePickerBase";
import {
    CaretIconLeft,
    CaretIconRight,
    MonthAndYearValue,
    MonthNavButton,
    StyledMonthYearSelector,
    TableCaretIconLeft,
    TableCaretIconRight
} from "./MonthYearSelector.styles";
import MonthYearPopup from "./popup/MonthYearPopup";
import TestIds from "../../../testIds";

interface IProps extends IPickerProps {
    isMain?: boolean
}

/** Shows month and a year, without input. MonthYearPopup can be opened by clicking on the label. */
class MonthYearSelector extends DatePickerClean<IProps> {
    static defaultProps: Partial<IPickerProps> = {
        openOnEnter: true
    };

    get pickerPopup() {
        return MonthYearPopup;
    }

    get inputComponent(): React.ComponentType<IInputProps<Date>> {
        return null;
    }

    get popoverOffsetX(): number {
        // center the popup
        return -10;
    }

    get popoverOffsetY(): number {
        return 10;
    }

    getCustomReferenceElement = (args: ICustomReferenceElement): React.ReactNode => {
        return this.renderValue(args);
    };

    handlePrevious = (): void => {
        const newDate = getUtcDayjs(this.props.value).subtract(1, "month");

        if (isDefined(this.props.minDate) && newDate.isBefore(this.props.minDate, "month")) {
            return;
        }

        this.props.onChange({
            value: newDate.toDate(),
            triggerAdditionalTasks: true
        });
    };

    handleNext = (): void => {
        const newDate = getUtcDayjs(this.props.value).add(1, "month");

        if (isDefined(this.props.maxDate) && newDate.isAfter(this.props.maxDate, "month")) {
            return;
        }

        this.props.onChange({
            value: newDate.toDate(),
            triggerAdditionalTasks: true
        });
    };

    handleKeyDown = (onKeyDown: (args: any) => void, e: React.KeyboardEvent): void => {
        if (e.key === KeyName.ArrowLeft) {
            this.handlePrevious();
        } else if (e.key === KeyName.ArrowRight) {
            this.handleNext();
        }
        onKeyDown(e);
    }

    renderValue = (args: ICustomReferenceElement): React.ReactElement => {
        const IconLeft = this.props.isMain ? TableCaretIconLeft : CaretIconLeft;
        const IconRight = this.props.isMain ? TableCaretIconRight : CaretIconRight;
        return (
                <StyledMonthYearSelector ref={composeRefHandlers(args.ref, args.openerRef)}
                                         data-testid={TestIds.YearSwitcher}
                                         tabIndex={0}
                                         onKeyDown={this.handleKeyDown.bind(this, args.onKeyDown)}
                                     $isMain={this.props.isMain}
                                     className={this.props.className}
                                     style={this.props.style}>
                    <MonthNavButton title={this.props.t("Components:Calendar.PreviousMonth")}
                                    onClick={this.handlePrevious}
                                    tabIndex={-1}
                                    isDisabled={this.props.minDate && getUtcDayjs(this.props.value).isSame(this.props.minDate, "month")}
                                isDecorative>
                    <IconLeft width={IconSize.M}/>
                </MonthNavButton>
                    <MonthAndYearValue onClick={args.onClick}>
                    {capitalize(DateType.format(this.props.value, DateFormat.monthAndYear))}
                </MonthAndYearValue>
                    <MonthNavButton title={this.props.t("Components:Calendar.NextMonth")}
                                    onClick={this.handleNext}
                                    tabIndex={-1}
                                    isDisabled={this.props.maxDate && getUtcDayjs(this.props.value).isSame(this.props.maxDate, "month")}
                                isDecorative>
                    <IconRight width={IconSize.M}/>
                </MonthNavButton>
            </StyledMonthYearSelector>
        );
    };
}

export default withTranslation(["Common", "Component"])(MonthYearSelector);